import React from 'react';
import { DownloadIcon } from 'lucide-react';
import { COURSES } from '../../../data/academyCourses';
import { SAMPLE_ROSTER, SAMPLE_WEEKLY_ENROLMENTS } from '../../../data/academyAdmin';
import { downloadCsv } from '../../../utils/academyFormat';
import { inputClass } from '../../ui/Field';
export function AdminOverview() {
  const [courseSlug, setCourseSlug] = React.useState(COURSES.find(c => c.flagship)?.slug ?? COURSES[0].slug);
  const learners = SAMPLE_ROSTER.filter(l => l.role === 'learner' || Object.keys(l.enrolled).length);
  const enrolments = learners.flatMap(l => Object.entries(l.enrolled).map(([slug, percent]) => ({
    learner: l,
    slug,
    percent
  })));
  const completions = enrolments.filter(e => e.percent === 100);
  const completionRate = enrolments.length ? Math.round(completions.length / enrolments.length * 100) : 0;
  const activeThisWeek = learners.filter(l => new Date('2026-10-12').getTime() - new Date(l.lastActive).getTime() <= 7 * 864e5).length;
  const peak = Math.max(...SAMPLE_WEEKLY_ENROLMENTS);
  const byCourse = COURSES.map(c => {
    const rows = enrolments.filter(e => e.slug === c.slug);
    const avg = rows.length ? Math.round(rows.reduce((s, r) => s + r.percent, 0) / rows.length) : 0;
    return {
      course: c,
      count: rows.length,
      avg
    };
  }).sort((a, b) => b.count - a.count);
  const course = COURSES.find(c => c.slug === courseSlug)!;
  const courseRows = enrolments.filter(e => e.slug === courseSlug);
  const dropOff = course.modules.map((m, i) => {
    const threshold = i / course.modules.length * 100;
    return {
      title: m.title,
      reached: courseRows.filter(r => r.percent > threshold || i === 0 && r.percent >= 0).length
    };
  });
  const exportEnrolments = () => downloadCsv('academy-enrolments.csv', [['Learner', 'Email', 'Country', 'Organisation', 'Course', 'Progress'], ...enrolments.map(e => [e.learner.name, e.learner.email, e.learner.country, e.learner.organisation, COURSES.find(c => c.slug === e.slug)?.title ?? e.slug, `${e.percent}%`])]);
  return <div className="space-y-12" data-id="element-247" data-mp="4aqm6efe6e3o">
      <dl className="grid grid-cols-2 gap-6 lg:grid-cols-4" data-id="element-248" data-mp="92i2zx8qx5gm">
        <Kpi label="Learners" value={learners.length} data-id="element-249" data-mp="942z3rapkwgl" />
        <Kpi label="Enrolments" value={enrolments.length} data-id="element-250" data-mp="3rhkacw2nbfft" />
        <Kpi label="Completion rate" value={`${completionRate}%`} data-id="element-251" data-mp="3rey1mt40qtw0" />
        <Kpi label="Active this week" value={activeThisWeek} data-id="element-252" data-mp="3viw1x752yaqn" />
      </dl>

      <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr]" data-id="element-253" data-mp="n2b1wfuferzo">
        <section aria-labelledby="trend" data-id="element-254" data-mp="s1nn6w5nfr05">
          <h2 id="trend" className="text-[16px] font-medium text-ink" data-id="element-255" data-mp="s0kz0vzpo2nx">
            New enrolments, last 12 weeks
          </h2>
          <div className="mt-5 flex h-48 items-end gap-2 border-b border-line" role="img" aria-label={`Weekly enrolments rising from ${SAMPLE_WEEKLY_ENROLMENTS[0]} to ${SAMPLE_WEEKLY_ENROLMENTS[SAMPLE_WEEKLY_ENROLMENTS.length - 1]}`} data-id="element-256" data-mp="e1l97l0gphq6">
            {SAMPLE_WEEKLY_ENROLMENTS.map((n, i) => <div key={i} className="flex flex-1 flex-col items-center justify-end gap-1.5" data-id="element-257" data-mp="33srlfjkfy7up" data-mpi={i}>
                <span className="text-[11px] text-muted" data-id="element-258" data-mp="2v8l8aye0xqb7">{n}</span>
                <div className={`w-full rounded-t-[3px] ${i === SAMPLE_WEEKLY_ENROLMENTS.length - 1 ? 'bg-gold' : 'bg-line'}`} style={{
              height: `${n / peak * 150}px`
            }} data-id="element-259" data-mp="2r9cg3xymu0hr" />
              </div>)}
          </div>
          <p className="mt-2 text-small text-muted" data-id="element-260" data-mp="30akesh5293cd">Most recent week highlighted.</p>
        </section>

        <section aria-labelledby="completion" data-id="element-261" data-mp="2ckgbkavsb5lw">
          <h2 id="completion" className="text-[16px] font-medium text-ink" data-id="element-262" data-mp="27klluanqwm2f">
            Average progress by course
          </h2>
          <ul className="mt-5 space-y-3" data-id="element-263" data-mp="2m9reffs2ve1c">
            {byCourse.slice(0, 6).map(({
            course: c,
            count,
            avg
          }) => <li key={c.slug} data-id="element-264" data-mp="2hhzo96cu3abq">
                <div className="flex justify-between gap-3 text-small" data-id="element-265" data-mp="2hgoln9v4k8m7">
                  <span className="truncate text-body" data-id="element-266" data-mp="1trm565c88lta">{c.title}</span>
                  <span className="shrink-0 text-ink" data-id="element-267" data-mp="1ouvr1njzg0ai">
                    {avg}% <span className="text-muted" data-id="element-268" data-mp="1os9h5h9md8p4">· {count}</span>
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-charcoal" data-id="element-269" data-mp="x1tjtynhpjvf">
                  <div className="h-full rounded-full bg-gold" style={{
                width: `${avg}%`
              }} data-id="element-270" data-mp="wzfqpvcdehqi" />
                </div>
              </li>)}
          </ul>
        </section>
      </div>

      <section aria-labelledby="dropoff" className="border-t border-line pt-10" data-id="element-271" data-mp="15w82mb78bcja">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-272" data-mp="1asylt7ebyo8x">
          <div data-id="element-273" data-mp="1fr8ixnjm7yx6">
            <h2 id="dropoff" className="text-[16px] font-medium text-ink" data-id="element-274" data-mp="1fqqcxz75i6c2">
              Module drop-off
            </h2>
            <p className="mt-1 text-small text-muted" data-id="element-275" data-mp="1kpa2e9kye096">How many enrolled learners reach each module. A sharp fall points to a content problem.</p>
          </div>
          <label htmlFor="dropoff-course" className="sr-only" data-id="element-276" data-mp="3mjsprv11obrz">
            Course
          </label>
          <select id="dropoff-course" value={courseSlug} onChange={e => setCourseSlug(e.target.value)} className={`${inputClass} w-auto max-w-xs py-2 text-[14px]`} data-id="element-277" data-mp="3ql495itbs0rq">
            {COURSES.map(c => <option key={c.slug} value={c.slug} data-id="element-278" data-mp="3e9bncvxvbczg">
                {c.title}
              </option>)}
          </select>
        </div>
        {courseRows.length ? <ol className="mt-6 space-y-2.5" data-id="element-279" data-mp="35a83vqusgvkf">
            {dropOff.map((m, i) => <li key={m.title} className="grid grid-cols-[1fr_auto] items-center gap-4 sm:grid-cols-[220px_1fr_auto]" data-id="element-280" data-mp="5u1rw6ohh60n" data-mpi={i}>
                <span className="truncate text-small text-body" data-id="element-281" data-mp="w9y174a39tt">
                  {i + 1}. {m.title}
                </span>
                <div className="hidden h-2 overflow-hidden rounded-full bg-charcoal sm:block" data-id="element-282" data-mp="3sxg7m4ckjwkc">
                  <div className="h-full rounded-full bg-royal" style={{
              width: `${m.reached / courseRows.length * 100}%`
            }} data-id="element-283" data-mp="3nzendg3epr7a" />
                </div>
                <span className="text-small text-ink" data-id="element-284" data-mp="2cl130l7v3tw0">
                  {m.reached} of {courseRows.length}
                </span>
              </li>)}
          </ol> : <p className="mt-6 text-[14px] text-muted" data-id="element-285" data-mp="2j0zurqf2xdds">No enrolments in this course yet.</p>}
      </section>

      <section aria-labelledby="reports" className="border-t border-line pt-10" data-id="element-286" data-mp="251rusvb3e18j">
        <h2 id="reports" className="text-[16px] font-medium text-ink" data-id="element-287" data-mp="292tvzhokknd5">
          Reports
        </h2>
        <button type="button" onClick={exportEnrolments} className="mt-3 inline-flex items-center gap-2 text-[14px] text-royal hover:text-ink" data-id="element-288" data-mp="1vbfan476wa2x">
          <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-289" data-mp="202xdjxsrwkqj" />
          Enrolments and progress, CSV
        </button>
      </section>
    </div>;
}
function Kpi({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div className="border-l-2 border-line pl-4" data-id="element-290" data-mp="1q9zpzd8xc5l2">
      <dt className="text-small text-muted" data-id="element-291" data-mp="1cdmag9nyae98">{label}</dt>
      <dd className="mt-1 font-display text-[34px] leading-none text-ink" data-id="element-292" data-mp="1hbo39mb6hrxw">{value}</dd>
    </div>;
}